import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import SEOHead from "@/components/SEOHead";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({ initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-50px" }, transition: { duration: 0.6, ease, delay } });
const stagger = (i) => ({ initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.55, ease, delay: i * 0.1 } });

const STEPS = [
  { num: "01", title: "Share Your Goals", subtitle: "Onboarding Session", desc: "We start with a focused session where you share your target roles, years of experience, preferred companies, location, and work authorization. This becomes the foundation of your entire application profile.", details: ["Target role alignment", "Experience & seniority level", "Preferred companies", "US work authorization", "Timeline and urgency"] },
  { num: "02", title: "Profile Optimization", subtitle: "Before the First Application", desc: "Before we submit a single application, we review and improve your resume, LinkedIn profile, and cover letter approach. We position you correctly — to pass ATS filters and make a strong first impression.", details: ["Resume review and restructuring", "LinkedIn headline and summary", "Cover letter templates", "ATS keyword optimization", "Job tracking setup"] },
  { num: "03", title: "Strategic Applications", subtitle: "Execution Begins", desc: "We research relevant open roles aligned with your profile and approved direction, then submit through official company portals. Every application is intentional and aligned with your preferences.", details: ["Official company portals only", "Role-fit review before applying", "Aligned with preferences", "Tracking every submission", "No spam or bulk tools"] },
  { num: "04", title: "Weekly Review", subtitle: "Ongoing Feedback Loop", desc: "At the end of each week, we share a summary of applications, any signals or responses, and our read on what's working. You provide feedback and we incorporate it immediately.", details: ["Application summary report", "Response tracking", "Feedback collection", "Direction refinements", "Market observations"] },
  { num: "05", title: "Refine & Improve", subtitle: "Continuous Strategy Evolution", desc: "We continuously refine based on response rates, market signals, role availability, and your evolving preferences. The process gets smarter over time.", details: ["Strategy adjustment based on data", "Market trend monitoring", "Role pattern recognition", "Ongoing profile improvements", "Iteration based on results"] },
];

const HowItWorksPage = () => (
  <div data-testid="how-it-works-page" className="bg-white">
    <SEOHead title="How It Works | ApplyStrat" description="Learn ApplyStrat's 5-step strategic tech job application process: onboarding, profile optimization, strategic applications, weekly review, and continuous refinement." canonical="https://applystrat.com/how-it-works" />

    <section className="pt-36 pb-16 bg-white text-center" style={{ background: "radial-gradient(circle at 50% -20%, rgba(255,82,0,0.12) 0%, transparent 65%)" }}>
      <div className="max-w-3xl mx-auto px-6">
        <motion.div {...fadeUp()}>
          <p className="section-label-orange mb-4">Process</p>
          <h1 className="text-5xl md:text-6xl font-medium tracking-tighter text-ink mb-6">How It Works</h1>
          <p className="text-lg text-ink-secondary max-w-xl mx-auto leading-relaxed">Five structured steps. Complete transparency. A process built to execute your job search with the same discipline you'd want applied to your career.</p>
        </motion.div>
      </div>
    </section>

    <section className="py-16 pb-28 bg-white">
      <div className="max-w-3xl mx-auto px-6 space-y-6">
        {STEPS.map((step, i) => (
          <motion.div key={step.num} {...stagger(i)} data-testid={`step-${step.num}`} className="bg-surface-raised rounded-3xl p-8 hover:bg-white hover:shadow-[0_8px_30px_rgba(255,82,0,0.10)] hover:-translate-y-0.5 transition-all duration-400">
            <div className="flex flex-col sm:flex-row sm:items-start gap-6">
              <div className="flex-shrink-0">
                <span className={`text-5xl font-medium text-brand/25 leading-none block animate-float-d${i}`}>{step.num}</span>
              </div>
              <div className="flex-1">
                <p className="text-brand text-xs font-medium uppercase tracking-widest mb-2">{step.subtitle}</p>
                <h2 className="text-ink text-xl font-medium mb-3">{step.title}</h2>
                <p className="text-ink-secondary text-sm leading-relaxed mb-5">{step.desc}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {step.details.map((d) => (
                    <div key={d} className="flex items-center gap-2">
                      <CheckCircle2 size={13} className="text-brand flex-shrink-0" />
                      <span className="text-ink-secondary text-xs">{d}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>

    <section className="py-20 bg-surface-raised border-t border-black/5">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <motion.div {...fadeUp()}>
          <h2 className="text-3xl font-medium text-ink mb-5">Ready to Get Started?</h2>
          <p className="text-ink-secondary text-base mb-8">Apply now and we'll review your profile within 24 hours.</p>
          <Button asChild size="lg" className="shadow-sm hover:scale-[1.02] hover:shadow-[0_8px_32px_rgba(255,82,0,0.40)]">
            <Link to="/apply" data-testid="how-it-works-apply-button">Apply to Work With Us <ArrowRight size={18} /></Link>
          </Button>

        </motion.div>
      </div>
    </section>
  </div>
);

export default HowItWorksPage;
